import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Tooltip,
    IconButton,
    TextField,
    MenuItem,
    Chip
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { ItemCategory } from '../../interfaces/item-category.interface';
import { ITEM_NATURE_LABEL, ItemNature } from '../../interfaces/item.interface';
import { ItemCategoryService } from '../../services/item-category.service';
import {AuthService} from "../../services/auth.service";
import { useToast } from '../../context/ToastContext';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { leafAccounts } from '../../utils/postingAccounts';
import SearchableSelect from '../common/SearchableSelect';
import { fetchAllPages } from '../../services/salesOrder.service';
import { sortCategoryTree } from '../../utils/itemCategoryTree';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const ItemCategoryList: React.FC = () => {
    const [itemCategories, setItemCategories] = useState<ItemCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const toast = useToast();
    // The consumption-account override is an accounting setting: store staff without ledger access neither load nor see it.
    const canViewAccounts = usePermission(PERMISSIONS.ACCOUNT_VIEW);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);

    const [selectedItemCategory, setSelectedItemCategory] = useState<ItemCategory | null>(null);
    const [formData, setFormData] = useState({
        itemCategoryName: '',
        description: '',
        institutionId: '',
        consumptionAccountId: '',
        parentId: '',
        // '' = same as the main category (sub-categories only)
        defaultNature: 'CONSUMABLE' as ItemNature | ''
    });
    const [accounts, setAccounts] = useState<Account[]>([]);
    // Every category is loaded so each sub-category can sit under its main category.
    const fetchItemCategories = async () => {
        try {
            setLoading(true);
            const all = await fetchAllPages((p, size) => ItemCategoryService.getItemCategoriesByInstitution(p, size));
            setItemCategories(sortCategoryTree(all));
        } catch (err) {
            toast.showApiError(err, 'Failed to load item categories');
            console.error('Error fetching item categories:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItemCategories();
    }, []);

    useEffect(() => {
        if (!canViewAccounts) return;
        AccountService.getAllAccounts().then(a => setAccounts(a ?? [])).catch(() => setAccounts([]));
    }, [canViewAccounts]);

    const handleOpenFormModal = (itemCategory?: ItemCategory) => {
        if (itemCategory) {
            setSelectedItemCategory(itemCategory);
            setFormData({
                itemCategoryName: itemCategory.itemCategoryName,
                description: itemCategory.description,
                institutionId: itemCategory.institutionId,
                consumptionAccountId: itemCategory.consumptionAccountId ?? '',
                parentId: itemCategory.parentId ?? '',
                defaultNature: (itemCategory.parentId ? (itemCategory.defaultNature ?? '') : (itemCategory.effectiveNature ?? itemCategory.defaultNature ?? 'CONSUMABLE')) as ItemNature | ''
            });
        } else {
            setSelectedItemCategory(null);
            setFormData({
                itemCategoryName: '',
                description: '',
                institutionId: '',
                consumptionAccountId: '',
                parentId: '',
                defaultNature: 'CONSUMABLE'
            });
        }
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setSelectedItemCategory(null);
        setFormData({
            itemCategoryName: '',
            description: '',
            institutionId: '',
            consumptionAccountId: '',
            parentId: '',
            defaultNature: 'CONSUMABLE'
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const sub = !!formData.parentId;
        const { defaultNature, ...rest } = formData;
        const payload = {
            ...rest,
            parentId: formData.parentId || null,
            // a sub-category left on "Same as main category" follows its parent; everything else sends its own type
            ...(sub && !defaultNature ? { sameAsParent: true } : { defaultNature: defaultNature || 'CONSUMABLE' }),
        };
        try {
            if (selectedItemCategory) {
                const saved = await ItemCategoryService.updateItemCategory(selectedItemCategory.id!, payload);
                toast.showSuccess('Category updated');
                if (saved?.warning) toast.showError(saved.warning);
            } else {
                payload.institutionId = AuthService.getSelectedInstitution();
                const saved = await ItemCategoryService.createItemCategory(payload);
                toast.showSuccess('Category created');
                if (saved?.warning) toast.showError(saved.warning);
            }
            await fetchItemCategories();
            handleCloseFormModal();
        } catch (error) {
            console.error('Error saving item category:', error);
            toast.showApiError(error, 'Failed to save item category');
        }
    };

    // Parent choices: main categories only, never the category itself; a category with sub-categories stays a main one.
    const parentOptions = itemCategories.filter(c => !c.parentId && c.id !== selectedItemCategory?.id);
    const hasSubs = !!selectedItemCategory && itemCategories.some(c => c.parentId === selectedItemCategory.id);
    const mainCount = itemCategories.filter(c => !c.parentId).length;

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <Box sx={{ p: 3 }} data-help-page="store.categories.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Item Category List</Typography>
                <Button
                    data-help="store.categories.create"
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => handleOpenFormModal()}
                >
                    Create Item Category
                </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Main categories: {mainCount} · Sub-categories: {itemCategories.length - mainCount}
            </Typography>

            <TableContainer component={Paper} data-help-section="store.categories.table">
                <Table size="small" data-testid="item-categories-table" sx={{ minWidth: 640 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
                            <TableCell data-help="store.categories.col.name">Category Name</TableCell>
                            <TableCell data-help="store.categories.col.parent">Parent Category</TableCell>
                            <TableCell data-help="store.categories.col.nature">Item type</TableCell>
                            <TableCell data-help="store.categories.col.description">Description</TableCell>
                            <TableCell data-help="store.categories.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {itemCategories.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">No item categories found</TableCell>
                            </TableRow>
                        ) : itemCategories.map(category => (
                            <TableRow key={category.id} hover data-testid={`item-category-row-${category.id}`}>
                                <TableCell sx={category.parentId ? { pl: 4.5 } : { fontWeight: 600 }}>
                                    {category.parentId ? `↳ ${category.itemCategoryName}` : category.itemCategoryName}
                                </TableCell>
                                <TableCell data-testid={`item-category-parent-${category.id}`}>
                                    {category.parentId
                                        ? category.parentName
                                        : <Typography component="span" variant="body2" color="text.secondary">Main category</Typography>}
                                </TableCell>
                                <TableCell data-testid={`item-category-nature-${category.id}`}>
                                    {category.effectiveNature ? ITEM_NATURE_LABEL[category.effectiveNature] : ''}
                                    {category.parentId && !category.defaultNature && (
                                        <Chip size="small" variant="outlined" label="same as main" sx={{ ml: 1 }} />
                                    )}
                                </TableCell>
                                <TableCell>{category.description}</TableCell>
                                <TableCell>
                                    <Tooltip title="Edit Category" arrow>
                                        <IconButton
                                            data-help="store.categories.action.edit"
                                            size="small"
                                            onClick={() => handleOpenFormModal(category)}
                                            color="primary"
                                            aria-label="Edit Category"
                                        >
                                            <Edit />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Form dialog */}
            <Dialog data-help-page="store.catForm.page"
                open={isFormModalOpen}
                onClose={handleCloseFormModal}
                fullWidth
                maxWidth="sm"
            >
                <form onSubmit={handleSubmit}>
                    <DialogTitle>{selectedItemCategory ? 'Edit Item Category' : 'Create Item Category'}</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            data-help="store.catForm.field.name" label="Category Name"
                            value={formData.itemCategoryName}
                            onChange={(e) => setFormData({ ...formData, itemCategoryName: e.target.value })}
                            required
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            data-help="store.catForm.field.description" label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            required
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <SearchableSelect<ItemCategory>
                            options={parentOptions}
                            value={formData.parentId}
                            onChange={id => setFormData({ ...formData, parentId: id })}
                            getOptionLabel={c => c.itemCategoryName}
                            data-help="store.catForm.field.parent" label="Parent category (optional)"
                            placeholder="None: this is a main category"
                            helperText={hasSubs
                                ? 'This category has sub-categories, so it stays a main category.'
                                : 'Pick a main category to make this a sub-category of it.'}
                            disabled={hasSubs}
                            clearable
                            fullWidth
                            sx={{ mt: 1, mb: 1 }}
                            data-testid="parent-category-select"
                        />
                        <TextField
                            select
                            fullWidth
                            data-help="store.catForm.field.nature" label="Item type"
                            value={formData.defaultNature}
                            onChange={(e) => setFormData({ ...formData, defaultNature: e.target.value as ItemNature | '' })}
                            margin="normal"
                            helperText={formData.parentId
                                ? 'Items in this sub-category use this type unless the item sets its own. "Same as main category" follows the main category.'
                                : 'Items in this category use this type unless the item sets its own.'}
                            data-testid="category-nature-select"
                        >
                            {formData.parentId && <MenuItem value="">Same as main category</MenuItem>}
                            <MenuItem value="CONSUMABLE">Consumable</MenuItem>
                            <MenuItem value="RETURNABLE">Returnable</MenuItem>
                            <MenuItem value="FIXED_ASSET">Fixed asset</MenuItem>
                        </TextField>
                        {canViewAccounts && <Box sx={{ mt: 1 }}>
                            <AccountAutocomplete
                                accounts={leafAccounts(accounts)}
                                value={formData.consumptionAccountId}
                                onChange={(id) => setFormData({ ...formData, consumptionAccountId: id })}
                                data-help="store.catForm.field.consumptionAccount" label="Consumption account (override)"
                                placeholder={formData.parentId ? "Use the parent category's account" : 'Use procurement-settings default'}
                                helperText={formData.parentId
                                    ? "Debited when items in this sub-category are issued. Leave empty to use the parent category's account."
                                    : 'Debited when items in this category are issued (e.g. WIP for fabric, office supplies for stationery).'}
                                fullWidth
                            />
                        </Box>}
                    </DialogContent>
                    <DialogActions>
                        <Button data-help="store.catForm.cancel" onClick={handleCloseFormModal}>
                            Cancel
                        </Button>
                        <Button data-help="store.catForm.save" type="submit" variant="contained" color="primary">
                            {selectedItemCategory ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default ItemCategoryList;
